import type { ApiResponseGetOrdersResponse, ApiResponseGetResubmissionResponse } from "../../../shared/types/api";
import { httpClient } from "../http-client";
import { NEW_API_ENDPOINTS } from "../../../shared/constants/api";
import type { ApiResponse } from "../../../shared/types/common";
import type { CreateOrderRequest, CreateOrderResponse, Courier } from "../../../domain/types/orders";
import type { BulkShipmentRegisterResponse, OrderSearchSuggestion } from "../../../domain/types/orders";

export class CompanyOrderApiService {
    async trackingOrder(companyId: number, orderId: number, trackingNumber: string, courier: string): Promise<ApiResponse> {
        const { url, body } = NEW_API_ENDPOINTS.companyOrderController.POST.trackingOrder(companyId, orderId, trackingNumber, courier);

        const response = await httpClient.post<ApiResponse>(url, body);

        if (response.data.message) {
            throw new Error(response.data.message || "재접수 변경에 실패했습니다.");
        }

        console.log("asdasd", response.data);
    
        return response.data;
    }
    async refreshTracking(companyId: number, orderId: number): Promise<ApiResponse> {
        const { url, body } = NEW_API_ENDPOINTS.companyOrderController.POST.refreshTracking(companyId, orderId);

        const response = await httpClient.post<ApiResponse>(url, body);

        if (response.data.message) {
            throw new Error(response.data.message || "배송 정보를 갱신하지 못했습니다.");
        }

        return response.data;
    }
    async getCourierList(): Promise<Courier[]> {
        const url = "/api/company/couriers";

        const response = await httpClient.get<{
            data: Courier[];
            errorMsg: string | null;
            errorCode: string | null;
        }>(url);

        if (response.data.errorCode) {
            throw new Error(response.data.errorMsg || "택배사 목록을 가져오지 못했습니다.");
        }

        return response.data.data;
    }

    async getOrderSearchSuggestions(companyId: number, keyword: string): Promise<OrderSearchSuggestion[]> {
        const response = await httpClient.get<{ data?: OrderSearchSuggestion[]; errorCode?: number; errorMsg?: string }>(
            `/api/company/${companyId}/orders/search-suggestions`, { params: { keyword } },
        );
        if (response.data.errorCode) throw new Error(response.data.errorMsg || "추천 검색어를 가져오지 못했습니다.");
        return response.data.data ?? [];
    }

    async getOrders(companyId: number, status?: string, keyword?: string, page?: number, size?: number, dateFrom?: string, dateTo?: string): Promise<ApiResponseGetOrdersResponse> {
        const response = await httpClient.get<ApiResponseGetOrdersResponse>(
            NEW_API_ENDPOINTS.companyOrderController.GET.list(companyId, status, keyword, page, size, dateFrom, dateTo),
        );

        if (response.data.errorCode) {
            throw new Error(response.data.errorMsg || "주문을 가져오지 못했습니다.");
        }

        return response.data;
    }

    async resubmission(companyId: number, orderId: number, memo: string): Promise<ApiResponse> {
        const { url, body } = NEW_API_ENDPOINTS.companyOrderController.POST.resubmission(companyId, orderId, memo);

        const response = await httpClient.post<ApiResponse>(url, body);

        if (response.data.message) {
            throw new Error(response.data.message || "재접수 변경에 실패했습니다.");
        }
    
        return response.data;
    }

    async resubmissionList(companyId: number): Promise< ApiResponseGetResubmissionResponse> {
        const response = await httpClient.get<ApiResponseGetResubmissionResponse>(
            NEW_API_ENDPOINTS.companyOrderController.GET.resubmissionList(companyId),
        );

        if (response.data.errorCode) {
            throw new Error(response.data.errorMsg || "주문을 가져오지 못했습니다.");
        }

        return response.data;
    }

    async acceptOrder(companyId: number, orderId: number): Promise<ApiResponse> {
        const { url, body } = NEW_API_ENDPOINTS.companyOrderController.PATCH.acceptOrder(companyId, orderId);

        const response = await httpClient.patch<ApiResponse>(url, body);

        if (response.data.message) {
            throw new Error(response.data.message || "재접수 변경에 실패했습니다.");
        }
    
        return response.data;
    }

    /**
     * 결제 완료 처리. 무통장입금 자동확인을 쓰지 않는 회사가
     * 은행에서 입금을 직접 확인한 뒤 부른다.
     */
    async confirmPayment(companyId: number, orderId: number, paymentMethod: string): Promise<ApiResponse> {
        const { url, body } = NEW_API_ENDPOINTS.companyOrderController.POST.confirmPayment(
            companyId,
            orderId,
            paymentMethod,
        );

        const response = await httpClient.post<ApiResponse>(url, body);

        return response.data;
    }

     async updateTrackingInfo(companyId: number, orderId: number, courier: string, trackingNumber: string): Promise<ApiResponse> {
        const { url, body } = NEW_API_ENDPOINTS.companyOrderController.POST.updateTrackingInfo(companyId, orderId, courier, trackingNumber);

        const response = await httpClient.post<ApiResponse>(url, body);

        if (response.data.message) {
            throw new Error(response.data.message || "재접수 변경에 실패했습니다.");
        }

        return response.data;
     }

     async startProduction(companyId: number, orderId: number): Promise<ApiResponse> {
        const url = `/api/company/${companyId}/orders/${orderId}/start-production`;

        const response = await httpClient.post<ApiResponse>(url);

        if (response.data.message) {
            throw new Error(response.data.message || "제작 시작에 실패했습니다.");
        }

        return response.data;
     }

     async completeProduction(companyId: number, orderId: number): Promise<ApiResponse> {
        const url = `/api/company/${companyId}/orders/${orderId}/complete-production`;

        const response = await httpClient.post<ApiResponse>(url);

        if (response.data.message) {
            throw new Error(response.data.message || "제작 완료 처리에 실패했습니다.");
        }

        return response.data;
     }

     async completeDelivery(companyId: number, orderId: number): Promise<ApiResponse> {
        const url = `/api/company/${companyId}/orders/${orderId}/complete-delivery`;

        const response = await httpClient.post<ApiResponse>(url);

        if (response.data.message) {
            throw new Error(response.data.message || "배송 완료 처리에 실패했습니다.");
        }

        return response.data;
     }

     async shipOrder(companyId: number, orderId: number, courier?: string, trackingNumber?: string): Promise<ApiResponse> {
        const url = `/api/company/${companyId}/orders/${orderId}/ship`;
        const body: { courier?: string; trackingNumber?: string } = {};

        if (courier) body.courier = courier;
        if (trackingNumber) body.trackingNumber = trackingNumber;

        const response = await httpClient.post<ApiResponse>(url, body);

        if (response.data.message) {
            throw new Error(response.data.message || "출고/배송 처리에 실패했습니다.");
        }

        return response.data;
     }

     async cancelOrder(companyId: number, orderId: number): Promise<ApiResponse> {
        const url = `/api/company/${companyId}/orders/${orderId}/cancel`;

        const response = await httpClient.post<ApiResponse>(url);

        if (response.data.message) {
            throw new Error(response.data.message || "주문 취소에 실패했습니다.");
        }

        return response.data;
     }

     async refundOrder(companyId: number, orderId: number): Promise<ApiResponse> {
        const url = `/api/company/${companyId}/orders/${orderId}/refund`;

        const response = await httpClient.post<ApiResponse>(url);

        if (response.data.message) {
            throw new Error(response.data.message || "환불 처리에 실패했습니다.");
        }

        return response.data;
     }

     async resetToBeforePayment(companyId: number, orderId: number): Promise<ApiResponse> {
        const url = `/api/company/${companyId}/orders/${orderId}/reset-to-before-payment`;

        const response = await httpClient.post<ApiResponse>(url);

        if (response.data.message) {
            throw new Error(response.data.message || "결제전 상태 변경에 실패했습니다.");
        }

        return response.data;
     }

     async bulkShipmentRegister(companyId: number, file: File): Promise<BulkShipmentRegisterResponse> {
        const url = NEW_API_ENDPOINTS.companyShippingController.POST.bulkRegister(companyId);
        const formData = new FormData();
        formData.append('file', file);

        const response = await httpClient.post<{
            data: BulkShipmentRegisterResponse;
            errorMsg: string | null;
            errorCode: string | null;
        }>(url, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        if (response.data.errorCode || !response.data.data) {
            throw new Error(response.data.errorMsg || "일괄 출고등록에 실패했습니다.");
        }

        return response.data.data;
     }

     async downloadShippingTemplate(companyId: number): Promise<Blob> {
        const url = NEW_API_ENDPOINTS.companyShippingController.GET.template(companyId);

        const response = await httpClient.get<Blob>(url, {
            responseType: 'blob',
        });

        return response.data;
     }

     async createOrder(companyId: number, orderData: CreateOrderRequest, orderSourceFile?: File): Promise<CreateOrderResponse> {
        console.log('[CreateOrder] Request data:', orderData);
        console.log('[CreateOrder] Company ID:', companyId);
        console.log('[CreateOrder] File:', orderSourceFile?.name);

        const formData = new FormData();

        // JSON payload를 blob으로 추가
        const payloadBlob = new Blob([JSON.stringify(orderData)], { type: 'application/json' });
        formData.append('payload', payloadBlob);

        // 파일이 있으면 추가
        if (orderSourceFile) {
            formData.append('orderSourceFile', orderSourceFile);
        }

        const url = `/api/company/${companyId}/orders/admin`;
        console.log('[CreateOrder] Request URL:', url);

        try {
            const response = await httpClient.post<{
                data: CreateOrderResponse;
                errorMsg: string | null;
                errorCode: string | null;
            }>(
                url,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            console.log('[CreateOrder] Response:', response.data);

            if (response.data.errorCode || !response.data.data) {
                throw new Error(response.data.errorMsg || "주문 생성에 실패했습니다.");
            }

            return response.data.data;
        } catch (error: any) {
            console.error('[CreateOrder] Error:', error);
            console.error('[CreateOrder] Error response:', error.response?.data);
            console.error('[CreateOrder] Error status:', error.response?.status);
            throw error;
        }
     }
}

export const companyOrderApiService = new CompanyOrderApiService();
